import { SearchResponseResponseBuilder } from 'gql/autogen/SearchResponseResponseBuilder';
import { PaginationResponseBuilder } from 'gql/autogen/PaginationResponseBuilder';
import { PricingSummaryResponseBuilder } from 'gql/autogen/PricingSummaryResponseBuilder';
import { PricingResponseBuilder } from 'gql/autogen/PricingResponseBuilder';
import { SearchActivityResultResponseBuilder } from 'gql/autogen/SearchActivityResultResponseBuilder';
import { ResultInfoResponseBuilder } from 'gql/autogen/ResultInfoResponseBuilder';
import { ReviewSummaryResponseBuilder } from 'gql/autogen/ReviewSummaryResponseBuilder';
import { ImageSizeResponseBuilder } from 'gql/autogen/ImageSizeResponseBuilder';
import { ImageResponseBuilder } from 'gql/autogen/ImageResponseBuilder';
import { ContentResponseBuilder } from 'gql/autogen/ContentResponseBuilder';
import { ActivityWithContentResponseBuilder } from 'gql/autogen/ActivityWithContentResponseBuilder';
import { DetailResponseBuilder } from 'gql/autogen/DetailResponseBuilder';
import { OfferDetailResponseBuilder } from 'gql/autogen/OfferDetailResponseBuilder';
import { SupplierInfoResponseBuilder } from 'gql/autogen/SupplierInfoResponseBuilder';
import { CancellationResponseBuilder } from 'gql/autogen/CancellationResponseBuilder';
import { buildPricingDisplayResponseBuilder } from 'gql/activityBuilder/pricingDisplayResponseQueryBuilder';
import { buildActivitySummaryResponseQuery } from 'gql/activityBuilder/activitySummaryResponseQueryBuilder';
import { buildSearchErrorResponse } from 'gql/activityBuilder/commonQueryBuilders';
import { CancellationPolicyResponseBuilder } from 'gql/autogen/CancellationPolicyResponseBuilder';
import { MatrixResponseBuilder } from 'gql/autogen/MatrixResponseBuilder';
import { FilterMatrixResponseBuilder } from 'gql/autogen/FilterMatrixResponseBuilder';
import { RangeFilterMatrixResponseBuilder } from 'gql/autogen/RangeFilterMatrixResponseBuilder';
import { ValueFilterMatrixResponseBuilder } from 'gql/autogen/ValueFilterMatrixResponseBuilder';
import { BucketMatrixResponseBuilder } from 'gql/autogen/BucketMatrixResponseBuilder';
import { ValueMatrixResponseBuilder } from 'gql/autogen/ValueMatrixResponseBuilder';
import { SortMatrixResponseBuilder } from 'gql/autogen/SortMatrixResponseBuilder';
import { RankScoreResponseBuilder } from 'gql/autogen/RankScoreResponseBuilder';
import { BadgesResponseBuilder } from 'gql/autogen/BadgesResponseBuilder';
import { BadgeResponseBuilder } from 'gql/autogen/BadgeResponseBuilder';
import { RepresentativeInfoResponseBuilder } from 'gql/autogen/RepresentativeInfoResponseBuilder';
import { PointOfInterestResponseBuilder } from 'gql/autogen/PointOfInterestResponseBuilder';

const build = (): SearchResponseResponseBuilder => {
    // GQL Server throws error when we use fragments at the moment, needs detailed analysis
    // const withChargeAmount = new ChargeAmountResponseBuilder().withChargeTotal();
    // const priceResponse = new PriceResponseBuilder().withExc().withInc();
    // const priceAmount = new PriceAmountResponseBuilder().withAmount();
    const resultInfo: ResultInfoResponseBuilder = new ResultInfoResponseBuilder()
        .withTotalActivities()
        .withTotalFilteredActivities()
        .withPagination(new PaginationResponseBuilder().withNumber().withSize())
        .withSelectedActivityId();

    return new SearchResponseResponseBuilder()
        .withIsSuccess()
        .withResult(
            new SearchActivityResultResponseBuilder()
                .withIsCompleted()
                .withResultInfo(resultInfo)
                .withActivities(
                    new ActivityWithContentResponseBuilder()
                        .withMasterActivityId()
                        .withMasterSupplierId()
                        .withRankScore(new RankScoreResponseBuilder().withValue())
                        .withActivityRepresentativeInfo(
                            new RepresentativeInfoResponseBuilder()
                                .withActivityId()
                                .withActivityToken()
                                .withPricingSummary(
                                    new PricingSummaryResponseBuilder().withPricing(new PricingResponseBuilder().withCurrency().withDisplay(buildPricingDisplayResponseBuilder())),
                                )
                                .withSupplierActivityCode()
                                .withCancellationPolicy(
                                    new CancellationResponseBuilder()
                                        .withCancellationType()
                                        .withPolicies(new CancellationPolicyResponseBuilder().withHoursFrom().withHoursUntil().withPenaltyCode()),
                                ),
                        )
                        .withContent(
                            new ContentResponseBuilder()
                                .withActivity(buildActivitySummaryResponseQuery())
                                .withImages(
                                    new ImageResponseBuilder().withUrl().withDescription().withImageType().withImageSize(new ImageSizeResponseBuilder().withHeight().withWidth()),
                                )
                                .withReviewSummary(new ReviewSummaryResponseBuilder().withAverageScore().withTotalCount())
                                .withDetail(
                                    new DetailResponseBuilder().withOfferDetails(
                                        new OfferDetailResponseBuilder().withSupplierInfo(new SupplierInfoResponseBuilder().withProviderName()),
                                    ),
                                )
                                .withContentLocale()
                                .withBadges(
                                    new BadgesResponseBuilder()
                                        .withBookingInfo(new BadgeResponseBuilder().withBadgeType().withEnrichmentData())
                                        .withActivityHighlights(new BadgeResponseBuilder().withBadgeType().withEnrichmentData())
                                        .withDeals(new BadgeResponseBuilder().withBadgeType().withEnrichmentData())
                                        .withKeyAttributes(new BadgeResponseBuilder().withBadgeType().withEnrichmentData())
                                        .withPromocodes(new BadgeResponseBuilder().withBadgeType().withEnrichmentData()),
                                ),
                        ),
                )
                .withMatrix(
                    new MatrixResponseBuilder()
                        .withSort(new SortMatrixResponseBuilder().withAvailableCode().withAvailableOrder())
                        .withFilter(
                            new FilterMatrixResponseBuilder()
                                .withRangeFilterMatrix(
                                    new RangeFilterMatrixResponseBuilder()
                                        .withCode()
                                        .withMin()
                                        .withMax()
                                        .withBucket(new BucketMatrixResponseBuilder().withCount().withMin().withMax()),
                                )
                                .withValueFilterMatrix(new ValueFilterMatrixResponseBuilder().withAvailableValue(new ValueMatrixResponseBuilder().withCount().withId()).withCode()),
                        ),
                )
                .withPointOfInterests(
                    new PointOfInterestResponseBuilder()
                        .withId()
                        .withName()
                        .withNameTranslated()
                        .withImageUrl()
                        .withActivities(
                            new ActivityWithContentResponseBuilder()
                                .withMasterActivityId()
                                .withMasterSupplierId()
                                .withRankScore(new RankScoreResponseBuilder().withValue())
                                .withActivityRepresentativeInfo(
                                    new RepresentativeInfoResponseBuilder()
                                        .withActivityId()
                                        .withActivityToken()
                                        .withPricingSummary(
                                            new PricingSummaryResponseBuilder().withPricing(
                                                new PricingResponseBuilder().withCurrency().withDisplay(buildPricingDisplayResponseBuilder()),
                                            ),
                                        )
                                        .withSupplierActivityCode()
                                        .withCancellationPolicy(
                                            new CancellationResponseBuilder()
                                                .withCancellationType()
                                                .withPolicies(new CancellationPolicyResponseBuilder().withHoursFrom().withHoursUntil().withPenaltyCode()),
                                        ),
                                )
                                .withContent(
                                    new ContentResponseBuilder()
                                        .withActivity(buildActivitySummaryResponseQuery())
                                        .withImages(
                                            new ImageResponseBuilder()
                                                .withUrl()
                                                .withDescription()
                                                .withImageType()
                                                .withImageSize(new ImageSizeResponseBuilder().withHeight().withWidth()),
                                        )
                                        .withReviewSummary(new ReviewSummaryResponseBuilder().withAverageScore().withTotalCount())
                                        .withDetail(
                                            new DetailResponseBuilder().withOfferDetails(
                                                new OfferDetailResponseBuilder().withSupplierInfo(new SupplierInfoResponseBuilder().withProviderName()),
                                            ),
                                        )
                                        .withContentLocale()
                                        .withBadges(
                                            new BadgesResponseBuilder()
                                                .withBookingInfo(new BadgeResponseBuilder().withBadgeType().withEnrichmentData())
                                                .withActivityHighlights(new BadgeResponseBuilder().withBadgeType().withEnrichmentData())
                                                .withDeals(new BadgeResponseBuilder().withBadgeType().withEnrichmentData())
                                                .withKeyAttributes(new BadgeResponseBuilder().withBadgeType().withEnrichmentData())
                                                .withPromocodes(new BadgeResponseBuilder().withBadgeType().withEnrichmentData()),
                                        ),
                                ),
                        ),
                ),
        )
        .withErrors(buildSearchErrorResponse());
};

export default build;
